import type { Gradient, GradientType } from './gradient';
import type { Guide } from './guides';
import type { LayerEffects } from './layerStyles';
import type { FilterId } from '../filters';
import type {
  PathOp,
  ShapeGeometry,
  ShapeStroke,
  StrokeAlign,
  Subpath,
  VectorPath,
} from '../vector/types';

/** Blend modes supported by the compositor. Order must match `BLEND_MODE_IDS`. */
export type BlendMode =
  | 'normal'
  | 'multiply'
  | 'screen'
  | 'overlay'
  | 'darken'
  | 'lighten'
  | 'colorDodge'
  | 'colorBurn'
  | 'hardLight'
  | 'softLight'
  | 'difference'
  | 'exclusion'
  | 'hue'
  | 'saturation'
  | 'color'
  | 'luminosity';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

/** Straight-alpha color, components in 0..1. */
export interface RGBA {
  r: number;
  g: number;
  b: number;
  a: number;
}

export type AdjustmentId =
  | 'brightnessContrast'
  | 'levels'
  | 'curves'
  | 'exposure'
  | 'vibrance'
  | 'hsl'
  | 'colorBalance'
  | 'blackAndWhite'
  | 'photoFilter'
  | 'channelMixer'
  | 'invert'
  | 'posterize'
  | 'threshold'
  | 'gradientMap'
  | 'selectiveColor'
  | 'shadowsHighlights'
  | 'desaturate';

/** Control points of a tone curve, in 0..1, ordered by x. At least two. */
export type Curve = readonly Point[];

/** Curves edits a composite curve and one per colour channel, as Photoshop does. */
export interface CurveChannels {
  rgb: Curve;
  r: Curve;
  g: Curve;
  b: Curve;
}

/**
 * Everything one adjustment is configured with. Sliders live in `values`; the
 * optional members are the editors that are not sliders, and only the
 * adjustments that declare them ever carry them.
 */
export interface AdjustmentState {
  id: AdjustmentId;
  values: Record<string, number>;
  curves?: CurveChannels;
  gradient?: Gradient;
  color?: RGBA;
}

export type LayerKind = 'raster' | 'text' | 'shape' | 'smart' | 'adjustment' | 'fill' | 'group';

/**
 * What a fill layer paints across the whole canvas. The layer itself has no
 * pixels: the compositor rebuilds this every frame, so edits stay live.
 */
export type FillContent =
  | { kind: 'solid'; color: RGBA }
  | {
      kind: 'gradient';
      gradient: Gradient;
      type: GradientType;
      /** Degrees, 0 pointing right. */
      angle: number;
      /** 1..150 as a percent of the canvas diagonal. */
      scale: number;
      reverse: boolean;
    }
  | {
      kind: 'pattern';
      patternId: string;
      /** Percent of the tile's native size. */
      scale: number;
    };

/**
 * Which result channels a layer may change after blending. Unchecked channels
 * keep the backdrop. When only `a` is on, luminance of the source is written
 * into alpha instead of the usual blended alpha.
 */
export interface BlendChannels {
  r: boolean;
  g: boolean;
  b: boolean;
  a: boolean;
}

export const DEFAULT_BLEND_CHANNELS: BlendChannels = {
  r: true,
  g: true,
  b: true,
  a: true,
};

/** Channel sampled by Advanced Blending → Blend If. */
export type BlendIfChannel = 'gray' | 'red' | 'green' | 'blue';

/**
 * One Blend If slider. Ends may be split (`black0`/`black1`, `white0`/`white1`)
 * for a soft transition; unsplit when the pair is equal. Values are 0..255.
 */
export interface BlendIfRange {
  black0: number;
  black1: number;
  white0: number;
  white1: number;
}

/** Photoshop-style Blend If for a layer or group. */
export interface BlendIf {
  channel: BlendIfChannel;
  thisLayer: BlendIfRange;
  underlying: BlendIfRange;
}

export const DEFAULT_BLEND_IF_RANGE: BlendIfRange = {
  black0: 0,
  black1: 0,
  white0: 255,
  white1: 255,
};

export const DEFAULT_BLEND_IF: BlendIf = {
  channel: 'gray',
  thisLayer: { ...DEFAULT_BLEND_IF_RANGE },
  underlying: { ...DEFAULT_BLEND_IF_RANGE },
};

/**
 * Fields every layer kind carries. Pixel data is never stored here — it lives in
 * a GPU texture owned by the engine and referenced by the layer id.
 */
interface LayerCommon {
  id: string;
  name: string;
  visible: boolean;
  /** 0..1 */
  opacity: number;
  blendMode: BlendMode;
  /** Advanced Blending channel restrict (R/G/B/A). */
  blendChannels: BlendChannels;
  /** Advanced Blending → Blend If. */
  blendIf: BlendIf;
  /** Blocks every edit, including moves. */
  locked: boolean;
  /** Blocks pixel edits while still allowing the layer to be moved. */
  lockPixels: boolean;
  /** Blocks moves and transforms while still allowing painting. */
  lockPosition: boolean;
  /** Blocks painting and edits on the raster mask. */
  lockMask: boolean;
  hasMask: boolean;
  maskEnabled: boolean;
  /**
   * A linked mask follows the layer when it is moved. Unlinking is what lets the
   * Move tool slide the mask over the pixels, or the pixels under the mask.
   */
  maskLinked: boolean;
  /** Clips this layer to the alpha of the nearest non-clipping layer below it. */
  clipping: boolean;
  /**
   * Vector mask. It is rasterised into the same mask texture a raster mask uses,
   * so the compositor needs to know nothing about it, but the path is kept so it
   * stays editable.
   */
  vectorMask: Subpath[] | null;
  /** Layer styles; null means none. Applied after the layer's own pixels. */
  effects: LayerEffects | null;
  /**
   * Placement of the layer texture in document pixels. Pure moves update this
   * instead of resampling, so content past the canvas edge is preserved.
   */
  contentOffset: Point;
}

/** Painted pixels. */
export interface RasterLayer extends LayerCommon {
  kind: 'raster';
}

export type TextAlign = 'left' | 'center' | 'right';

/** Editable text, rasterised into its surface on every change. */
export interface TextLayer extends LayerCommon {
  kind: 'text';
  content: string;
  fontFamily: string;
  /** Em-size in document pixels. */
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  fontStyle: 'normal' | 'italic';
  align: TextAlign;
  color: RGBA;
  /** Baseline origin of the first line, in document pixels. */
  x: number;
  y: number;
}

/** Fill of a shape layer; `none` is what leaves only the stroke visible. */
export type ShapePaint =
  | { kind: 'none' }
  | { kind: 'solid'; color: RGBA }
  | { kind: 'gradient'; gradient: Gradient; angle: number };

/**
 * Vector shape. The geometry is the source of truth and is rasterised into the
 * layer's surface on every change, so the shape stays crisp after a transform
 * and after the document is resized.
 */
export interface ShapeLayer extends LayerCommon {
  kind: 'shape';
  geometry: ShapeGeometry;
  fill: ShapePaint;
  stroke: (ShapeStroke & { color: RGBA }) | null;
}

/** One non-destructive filter on a smart object, reapplied from the original. */
export interface SmartFilter {
  id: string;
  filter: FilterId;
  values: Record<string, number>;
  enabled: boolean;
}

/**
 * Embedded raster content whose unfiltered pixels stay on the GPU. Filters in
 * `smartFilters` are replayed every composite from that original.
 */
export interface SmartLayer extends LayerCommon {
  kind: 'smart';
  smartFilters: SmartFilter[];
}

/** Non-destructive correction applied to everything composited below it. */
export interface AdjustmentLayer extends LayerCommon {
  kind: 'adjustment';
  adjustment: AdjustmentState;
}

/** Solid, gradient or pattern covering the canvas, editable from Properties. */
export interface FillLayer extends LayerCommon {
  kind: 'fill';
  fill: FillContent;
}

export interface GroupLayer extends LayerCommon {
  kind: 'group';
  /** Index 0 is the bottom-most child. */
  children: Layer[];
  expanded: boolean;
}

export type Layer =
  | RasterLayer
  | TextLayer
  | ShapeLayer
  | SmartLayer
  | AdjustmentLayer
  | FillLayer
  | GroupLayer;

/** Kinds backed by a GPU surface of their own. */
export type SurfaceLayer = RasterLayer | TextLayer | ShapeLayer | SmartLayer;

/**
 * A single-channel bitmap stored beside the colour channels, the way Photoshop
 * keeps alpha channels. Quick Mask is one of these too: it only differs in being
 * temporary and in being written back to the selection when it is switched off.
 */
export interface AlphaChannel {
  id: string;
  name: string;
  kind: 'alpha' | 'quickMask';
  /** Whether the channel is drawn over the colour ones. */
  visible: boolean;
  /** Colour of the overlay, with `a` as its strength. */
  color: RGBA;
}

/** Which of the colour channels the view shows. */
export interface ColorChannelVisibility {
  r: boolean;
  g: boolean;
  b: boolean;
}

/** What a channel row in the Channels panel points at. */
export type ChannelRef =
  | { kind: 'composite' }
  | { kind: 'color'; index: 0 | 1 | 2 }
  | { kind: 'mask'; layerId: string }
  | { kind: 'alpha'; id: string };

export interface DocumentMeta {
  id: string;
  name: string;
  width: number;
  height: number;
  /** Index 0 is the bottom-most layer. Groups nest through `children`. */
  layers: Layer[];
  activeLayerId: string | null;
  /** True while a non-empty selection mask exists for this document. */
  hasSelection: boolean;
  /** Index 0 is the top row of the Channels panel's alpha section. */
  channels: AlphaChannel[];
  colorVisible: ColorChannelVisibility;
  /** Saved paths, the rows of the Paths panel. Index 0 is the top row. */
  paths: VectorPath[];
  /** The path being edited; `null` means the work path is empty. */
  activePathId: string | null;
  /** Guides belong to the document, so they survive a reload with it. */
  guides: Guide[];
  dirty: boolean;
}

export interface ViewState {
  /** Pixels on screen per document pixel. */
  zoom: number;
  /** Document-space point pinned to the centre of the viewport. */
  centerX: number;
  centerY: number;
  /** Rotate View angle in degrees, clockwise on screen. */
  rotation: number;
}

/** Tools driven by the brush engine; each keeps its own set of `PaintOptions`. */
export type PaintToolId =
  | 'brush'
  | 'pencil'
  | 'eraser'
  | 'clone'
  | 'blur'
  | 'sharpen'
  | 'smudge'
  | 'dodge'
  | 'burn'
  | 'sponge'
  | 'spotHealing'
  | 'healing';

export const PAINT_TOOL_IDS = [
  'brush',
  'pencil',
  'eraser',
  'clone',
  'blur',
  'sharpen',
  'smudge',
  'dodge',
  'burn',
  'sponge',
  'spotHealing',
  'healing',
] as const satisfies readonly PaintToolId[];

export function isPaintTool(tool: string): tool is PaintToolId {
  return (PAINT_TOOL_IDS as readonly string[]).includes(tool);
}

/** Paint tools whose stamps carry the foreground colour rather than a filter. */
export const COLOR_PAINT_TOOLS = ['brush', 'pencil', 'eraser'] as const;

export type ToolId =
  | 'move'
  | 'selectRect'
  | 'selectEllipse'
  | 'lasso'
  | 'lassoPolygon'
  | 'lassoMagnetic'
  | 'magicWand'
  | 'quickSelect'
  | 'crop'
  | 'transform'
  | PaintToolId
  | 'patch'
  | 'gradient'
  | 'bucket'
  | 'pen'
  | 'penFree'
  | 'pathSelect'
  | 'directSelect'
  | 'shapeRect'
  | 'shapeEllipse'
  | 'shapeLine'
  | 'shapePolygon'
  | 'type'
  | 'eyedropper'
  | 'hand'
  | 'zoom';

/**
 * What a vector tool produces. Photoshop calls these the tool's three modes: a
 * shape layer, a path in the Paths panel, or pixels painted into the layer.
 */
export type VectorMode = 'shape' | 'path' | 'pixels';

export type SelectionCombineMode = 'replace' | 'add' | 'subtract' | 'intersect' | 'exclude';

/**
 * Which corner movements a transform session allows. Free scales and rotates,
 * the others match the entries in Photoshop's Edit - Transform submenu.
 */
export type TransformMode = 'free' | 'skew' | 'distort' | 'perspective' | 'warp';

/**
 * The point a transform scales and rotates around, picked in the nine-cell grid
 * of the options bar.
 */
export type TransformAnchor =
  | 'topLeft'
  | 'top'
  | 'topRight'
  | 'left'
  | 'center'
  | 'right'
  | 'bottomLeft'
  | 'bottom'
  | 'bottomRight';

/** Anchors in reading order, and where each sits in the unit square. */
export const TRANSFORM_ANCHORS: readonly { id: TransformAnchor; u: number; v: number }[] = [
  { id: 'topLeft', u: 0, v: 0 },
  { id: 'top', u: 0.5, v: 0 },
  { id: 'topRight', u: 1, v: 0 },
  { id: 'left', u: 0, v: 0.5 },
  { id: 'center', u: 0.5, v: 0.5 },
  { id: 'right', u: 1, v: 0.5 },
  { id: 'bottomLeft', u: 0, v: 1 },
  { id: 'bottom', u: 0.5, v: 1 },
  { id: 'bottomRight', u: 1, v: 1 },
];

/** Where a paint stroke goes when the active layer has a mask. */
export type PaintTargetKind = 'layer' | 'mask';

/** Colours Photoshop uses for the mask overlay of a channel. */
export const CHANNEL_OVERLAY_COLOR: RGBA = { r: 1, g: 0, b: 0, a: 0.5 };

export interface ToolOptions {
  /** 0..1 tolerance for the flood fill. */
  bucketTolerance: number;
  bucketContiguous: boolean;
  selectionMode: SelectionCombineMode;
  /** Feather radius in document pixels. */
  selectionFeather: number;
  selectionAntialias: boolean;
  /** 0..1 colour distance the Magic Wand accepts. */
  wandTolerance: number;
  wandContiguous: boolean;
  /** Sample the composite rather than the active layer alone. */
  wandSampleAllLayers: boolean;
  /** Brush diameter of the Quick Selection tool, in document pixels. */
  quickSelectSize: number;
  /** How far the Magnetic Lasso looks for an edge, in document pixels. */
  magneticWidth: number;
  /** 0..1 edge strength the Magnetic Lasso considers worth following. */
  magneticContrast: number;
  /** Distance in document pixels between automatic anchors. */
  magneticFrequency: number;
  transformKeepAspect: boolean;
  transformMode: TransformMode;
  transformAnchor: TransformAnchor;
  eyedropperFromComposite: boolean;
  /** Whether a vector tool makes a shape layer, a path, or pixels. */
  vectorMode: VectorMode;
  /** How a new subpath combines with the ones already in the shape. */
  pathOp: PathOp;
  /** Corner radius of the rectangle tool, in document pixels. */
  shapeRadius: number;
  shapeSides: number;
  /** Below 1 the polygon tool draws a star with that inner radius ratio. */
  shapeStarRatio: number;
  shapeFillEnabled: boolean;
  shapeStrokeEnabled: boolean;
  shapeStrokeWidth: number;
  shapeStrokeAlign: StrokeAlign;
  /** How closely the Free Pen follows the drawn line, in document pixels. */
  penTolerance: number;
  /** Rubber band preview of the next Pen segment. */
  penRubberBand: boolean;
  /** Defaults applied when the Type tool creates a new text layer. */
  textFontFamily: string;
  textFontSize: number;
  textAlign: TextAlign;
  textBold: boolean;
  textItalic: boolean;
}

export interface ExportSettings {
  format: 'png' | 'jpeg' | 'webp' | 'pdf';
  /** 0..1, ignored for png. */
  quality: number;
  scale: number;
}
